<template>
  <v-sheet outlined class="rounded overflow-hidden publication-history" color="bg1">
    <div class="publication-history__header">
      <div>
        <div class="ds-dialog__title">Publication history</div>
        <div class="publication-history__subtitle">
          Every time these notes were published or re-notified, and what happened to each email.
        </div>
      </div>
      <v-spacer />
      <v-btn icon small :loading="loading" title="Refresh" @click="fetchPublications">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-divider />

    <v-data-table
      class="elevation-0 rounded-0 custom-table"
      :headers="headers"
      :items="publications"
      :loading="loading"
      :expanded.sync="expanded"
      item-key="rowKey"
      show-expand
      hide-default-footer
      disable-pagination>
      <template v-slot:[`item.publishedAt`]="{ item }">
        <span v-if="publishedAtOf(item)">
          {{ $formatDate(publishedAtOf(item), 'DD/MM/YYYY HH:mm') }}
        </span>
        <span v-else class="publication-history__muted">—</span>
      </template>

      <template v-slot:[`item.recipientPolicy`]="{ item }">
        {{ policyLabelOf(item) }}
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip x-small :color="statusMetaOf(item).color" text-color="white">
          {{ statusMetaOf(item).label }}
        </v-chip>
      </template>

      <template v-slot:[`item.counts`]="{ item }">
        <span class="publication-history__counts">
          <span class="publication-history__count">{{ countOf(item, 'sent') }} sent</span>
          <span class="publication-history__count publication-history__count--failed">
            {{ countOf(item, 'failed') }} failed
          </span>
          <span class="publication-history__count">
            {{ countOf(item, 'suppressed') }} suppressed
          </span>
        </span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          v-if="canRetry(item)"
          x-small
          outlined
          color="warning"
          :loading="retryingId === item.rowKey"
          @click.stop="retryFailed(item)">
          <v-icon left x-small>mdi-refresh</v-icon>
          Retry failed
        </v-btn>
      </template>

      <!-- Vuetify only renders this slot for rows that are actually expanded, so the deliveries
           request for a publication is never issued while its row is collapsed. -->
      <template v-slot:expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="publication-history__expanded">
          <deliveries-table :publication-id="item.id" />
        </td>
      </template>

      <template v-slot:no-data>
        <div class="text-center d-flex flex-column">
          <img width="200" src="/img/notFound.svg" class="py-4 my-4" style="opacity: 0.54" />
          <p class="font-weight-medium">This release has not been published yet.</p>
        </div>
      </template>

      <template v-slot:loading>
        <div class="text-center d-flex flex-column">
          <img width="200" src="/img/loading.svg" class="py-4 my-4" style="opacity: 0.54" />
          <p class="font-weight-medium">Loading publications...</p>
        </div>
      </template>
    </v-data-table>
  </v-sheet>
</template>

<script>
  import { $formatDate, $has } from '@Utils';
  import { warn } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import DeliveriesTable from '@ReleaseNotes/components/DeliveriesTable.vue';
  import {
    ReleasePublicationStatus,
    ReleasePublicationStatusMeta,
    ReleaseRecipientPolicyLabels,
  } from '@ReleaseNotes/config/enums';

  const UNKNOWN_STATUS = { label: 'Unknown', color: 'grey' };

  /**
   * PublicationHistory — self-fetching list of `GET {id}/publications` with a lazy per-row
   * delivery drill-down.
   *
   * Rows are rendered from a locally normalised copy: the endpoint's shape is unverified
   * (addendum §A.5, assumed a bare array), and each row is given a stable `rowKey` so
   * `show-expand` still works if the DTO ever omits `id`.
   */
  export default {
    name: 'PublicationHistory',

    components: { DeliveriesTable },

    props: {
      releaseId: { type: [String, Number], default: null },
    },

    data() {
      return {
        permissions,
        publications: [],
        expanded: [],
        retryingId: null,
        headers: [
          { text: 'Published', value: 'publishedAt', width: '170px', sortable: false },
          { text: 'Recipients', value: 'recipientPolicy', width: '200px', sortable: false },
          { text: 'Status', value: 'status', width: '160px', sortable: false },
          { text: 'Deliveries', value: 'counts', sortable: false },
          { text: '', value: 'actions', align: 'right', width: '130px', sortable: false },
          { text: '', value: 'data-table-expand', width: '48px', sortable: false },
        ],
        requestSeq: 0,
      };
    },

    computed: {
      loading() {
        return this.$store.getters['RELEASE_NOTES/IS_PUBLICATIONS_LOADING'];
      },
    },

    watch: {
      releaseId() {
        this.expanded = [];
        this.fetchPublications();
      },
    },

    created() {
      this.fetchPublications();
    },

    beforeDestroy() {
      // Invalidate any in-flight response so it cannot write into a destroyed component.
      this.requestSeq += 1;
    },

    methods: {
      $has,
      $formatDate,

      /**
       * Normalises an unverified list response.
       * @param {*} data raw response body
       * @returns {{ items: Array, total: number }}
       */
      unwrapList(data) {
        if (Array.isArray(data)) return { items: data, total: data.length };
        if (data && typeof data === 'object') {
          const items = Array.isArray(data.items) ? data.items : [];
          return {
            items,
            total: Number.isFinite(Number(data.total)) ? Number(data.total) : items.length,
          };
        }
        return { items: [], total: 0 };
      },

      statusMetaOf(item) {
        return ReleasePublicationStatusMeta[item && item.status] || UNKNOWN_STATUS;
      },

      policyLabelOf(item) {
        const policy = item && item.recipientPolicy;
        return ReleaseRecipientPolicyLabels[policy] || '—';
      },

      publishedAtOf(item) {
        const row = item || {};
        return row.publishedAt || row.createdAt || row.createDate || row.queuedAt || null;
      },

      /**
       * Delivery counter, probed defensively across the plausible spellings.
       * @param {object} item publication row
       * @param {'sent'|'failed'|'suppressed'} kind
       */
      countOf(item, kind) {
        const row = item || {};
        const map = {
          sent: [row.sentCount, row.sent, row.deliveredCount],
          failed: [row.failedCount, row.failed],
          suppressed: [row.suppressedCount, row.suppressed],
        };
        const found = (map[kind] || []).find(
          (value) => Number.isFinite(Number(value)) && value !== null,
        );
        return found === undefined ? 0 : Number(found);
      },

      canRetry(item) {
        return (
          !!item &&
          item.status === ReleasePublicationStatus.CompletedWithFailures &&
          item.id != null &&
          $has(permissions.ReleaseNotes.Publish)
        );
      },

      async fetchPublications() {
        if (this.releaseId == null) return;

        this.requestSeq += 1;
        const seq = this.requestSeq;

        try {
          const data = await this.$store.dispatch(
            'RELEASE_NOTES/FETCH_PUBLICATIONS',
            this.releaseId,
          );
          if (seq !== this.requestSeq) return;
          const { items } = this.unwrapList(data);
          this.publications = items.map((row, index) => ({
            ...row,
            rowKey: row && row.id != null ? String(row.id) : `publication-${index}`,
          }));
        } catch (err) {
          if (seq !== this.requestSeq) return;
          warn('[PublicationHistory] failed to load publications', err);
        }
      },

      async retryFailed(item) {
        if (!this.canRetry(item)) return;

        const confirmed = await this.$dialog.confirm({
          title: 'Retry failed deliveries',
          text: 'Re-send this release only to the recipients whose email previously failed. Recipients who already received it are not emailed again.',
          actions: {
            false: 'Cancel',
            true: { text: 'Retry', color: 'warning' },
          },
        });
        if (!confirmed) return;

        this.retryingId = item.rowKey;
        try {
          await this.$store.dispatch('RELEASE_NOTES/RETRY_FAILED', item.id);
          this.$dialog.notify.success('Retrying the failed deliveries', {
            position: 'top-right',
            timeout: 3000,
          });
          await this.fetchPublications();
        } catch (err) {
          // RETRY_FAILED already toasted through $handleError.
          warn('[PublicationHistory] retry-failed rejected', err);
        } finally {
          this.retryingId = null;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .publication-history__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
  }

  .publication-history__subtitle {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: 2px;
  }

  .publication-history__counts {
    display: inline-flex;
    gap: 12px;
    font-size: 0.75rem;
  }

  .publication-history__count--failed {
    color: var(--v-error-base);
  }

  .publication-history__expanded {
    padding: 0 12px 12px !important;
  }

  .publication-history__muted {
    opacity: 0.5;
  }
</style>
